// A local game's clock: the pure functions in game/clock.ts bound to a time
// source, so LocalMatch reads and advances it without threading `now` around.
import {
  afterMove, createClock, flaggedSide, pauseClock, remainingAt, resumeClock, type ClockState,
} from '../game/clock.js';
import type { Color } from '../game/types.js';

// Bot think budget under a running clock: a share of what it has left, never below a floor.
const EXPECTED_MOVES_LEFT = 30;
const MIN_BUDGET_MS = 150;

export interface ClockReading {
  readonly w: number;
  readonly b: number;
  /** Whose clock is running, or null while stopped. */
  readonly running: Color | null;
}

export class MatchClock {
  private state: ClockState;

  constructor(private readonly totalMs: number, private readonly now: () => number) {
    this.state = createClock(totalMs);
  }

  reset(): void {
    this.state = createClock(this.totalMs);
  }

  reading(): ClockReading {
    const now = this.now();
    const s = this.state;
    return { w: remainingAt(s, 'w', now), b: remainingAt(s, 'b', now), running: s.runningSince === null ? null : s.turn };
  }

  moved(): void {
    this.state = afterMove(this.state, this.now());
  }

  pause(): void {
    this.state = pauseClock(this.state, this.now());
  }

  resume(): void {
    this.state = resumeClock(this.state, this.now());
  }

  flagged(): Color | null {
    return flaggedSide(this.state, this.now());
  }

  /** How long the side to move may think. */
  budgetFor(color: Color): number {
    return Math.max(MIN_BUDGET_MS, remainingAt(this.state, color, this.now()) / EXPECTED_MOVES_LEFT);
  }
}
